<template>
    <div class="w-100 h-100">
        <div class="row h-100">
            <div class="col-lg-3 h-100 d-flex flex-column np-overflow-hidden">
                <div class="np-widgets-panel np-pb-3">
                    <div class="np-widget-100">
                        <div class="np-group justify-content-between">
                            <h3 class="np-header3 np-pb-2">{{ this.project.name }}</h3>
                            <button class="np-btn-link" @click="showModProject">
                                <svg><use href="@/assets/images/icons/cog.svg#icon"></use></svg>
                            </button>
                        </div>
                        <span class="np-pb-1">{{ $t("UI.project.complete") }}: {{ this.projectProgress }}%</span>
                        <div class="np-progressbar">
                            <div class="np-progressbar-track" :style="{'width': (this.projectProgress) +'%', 'max-width': '100%'}"></div>
                        </div> 
                        
                        <div class="np-group align-items-center justify-content-between np-pt-2"  v-if="this.deadlineProject">
                            <h3 class="np-font-m np-lh-m np-pr-2">{{ $t("UI.dashboard.projectDeadline") }}: {{ localeDate(this.deadlineProject) }}</h3>                        
                            <IndicatorDaysTo :dateStart="this.project.date_planned_start" :dateEnd="this.deadlineProject" v-if="this.deadlineProject" classes="np-transparent np-borderless"/>
                        </div>
                    </div>                    
                </div>
                <div class="np-group np-pt-1 np-pb-4">
                    <div class="np-header2 vertical-align-center np-flex-1">
                        <svg class="np-mr-2"><use href="@/assets/images/icons/calendar-empty.svg#icon"></use></svg> {{ $t("UI.dashboard.tasks") }}
                    </div>
                    <button class="np-btn np-outline">
                        <svg><use href="@/assets/images/icons/filter.svg#icon"></use></svg>
                    </button>
                </div>
                <div class="np-accordion-group np-flex-1">
                    <template v-for="section in accordionSections" :key="section.id">
                        <Accordion 
                            :id="section.id"
                            :options="accordionOptions[section.id]"
                            :onClick="() => { toggleAccordion(section.id) }"
                        >
                            <template v-slot:header>
                                <div class="d-flex flex-grow-1">
                                    <span class="np-font-ml np-f-medium me-1">{{ section.title }}</span>
                                    <span v-if="section.tasks.length > 0" class="np-badge-s np-inactive ms-1">{{ section.tasks.length }}</span>
                                </div>
                            </template>
                            <SimpleBar data-simplebar-auto-hide="true" style="max-height: 100%;">
                                <ul class="np-list px-4 pb-4">
                                    <li class="pb-2" v-for="task in section.tasks" :key="task.id">
                                        <TaskCardAccordion
                                            :task="getPreparedTask(task)"
                                            :status="getTaskStatus(task)"
                                            @showModTask="showModTask"
                                        />
                                    </li>   
                                </ul>
                            </SimpleBar>
                        </Accordion> 
                    </template>
                </div>
            </div>

            <div class="col-lg-6 h-100 d-flex flex-column">
                    <div class="np-group align-items-center justify-content-between pb-3">
                        <div class="np-header2 vertical-align-center">
                            <svg class="np-mr-2"><use href="@/assets/images/icons/calendar-empty.svg#icon"></use></svg> {{ $t("UI.dashboard.tasksSummary") }}
                        </div>
                        <div class="np-panel">
                            <div class="np-panel-body py-1 px-2 flex-row">
                                <svg class="icon"><use href="@/assets/images/icons/eye2.svg#icon"></use></svg> 
                                <span class="np-font-m np-lh-xxl me-2">{{ $t("UI.dashboard.showMyTasks") }}</span>
                                <div class="form-check form-switch">
                                    <input class="form-check-input" type="checkbox" role="switch" id="switchOnlyMyTasks" v-model="onlyMyTasks">
                                </div> 
                            </div>
                        </div>
                    </div>
                    <div class="np-widgets-panel np-pb-4">
                        <div class="np-widget">
                            <div class="np-widget-top">
                                <p class="np-font-l np-f-medium np-lh-l">{{ this.tasksObject.completed.length }} {{ $t("UI.dashboard.from") }} {{ this.taskList?.length }}</p>
                                <svg class="np-color-success"><use href="@/assets/images/icons/calendar-complete.svg#icon"></use></svg>
                            </div>
                            <div class="np-widget-bottom">
                                <span class="np-lh-m">{{ $t("UI.dashboard.toCompleted") }}</span>
                                <!-- <svg style="width: 20px; height: 20px;"><use href="@/assets/images/icons/eye20.svg#icon"></use></svg> -->
                            </div>
                        </div>
                        
                        <div class="np-widget">
                            <div class="np-widget-top">
                                <p class="np-font-l np-f-medium np-lh-l">{{ this.tasksObject.today.length }}</p>
                                <svg><use href="@/assets/images/icons/calendar-empty.svg#icon"></use></svg>
                            </div>                        
                            <div class="np-widget-bottom">
                                <span class="np-lh-m">{{ $t("UI.dashboard.toToday") }}</span>
                                <!-- <svg style="width: 20px; height: 20px;"><use href="@/assets/images/icons/eye20.svg#icon"></use></svg> -->
                            </div>
                        </div>

                        <div class="np-widget">
                            <div class="np-widget-top">
                                <p class="np-font-l np-f-medium np-lh-l">{{ this.tasksObject.expired.length }}</p>
                                <svg class="np-color-amber"><use href="@/assets/images/icons/calendar-expire.svg#icon"></use></svg>
                            </div>                        
                            <div class="np-widget-bottom">
                                <span class="np-lh-m">{{ $t("UI.dashboard.toExpired") }}</span>
                                <!-- <svg style="width: 20px; height: 20px;"><use href="@/assets/images/icons/eye20.svg#icon"></use></svg> -->
                            </div>
                        </div>

                        <div class="np-widget">
                            <div class="np-widget-top">
                                <p class="np-font-l np-f-medium np-lh-l">{{ this.tasksObject.failed.length }}</p>
                                <svg class="np-color-danger"><use href="@/assets/images/icons/calendar-fail.svg#icon"></use></svg>
                            </div>                        
                            <div class="np-widget-bottom">
                                <span class="np-lh-m">{{ $t("UI.dashboard.toFailed") }}</span>
                                <!-- <svg style="width: 20px; height: 20px;"><use href="@/assets/images/icons/eye20.svg#icon"></use></svg> -->
                            </div>
                        </div>
                    </div>

                    <div class="np-panel flex-grow-1">
                        <div class="np-panel-header justify-content-between">
                            <div class="np-group">
                                <!--
                                <button class="np-btn np-outline np-mr-2">
                                    <svg><use href="@/assets/images/icons/chart-line.svg#icon"></use></svg>
                                </button>                        
                                -->
                                <button class="np-btn np-outline np-mr-3">
                                    <svg><use href="@/assets/images/icons/chart-bar.svg#icon"></use></svg>
                                </button> 
                                <div class="np-p-2" v-if="this.maxBarSteps > 0">
                                    <vue-slider v-model="this.barChartScale" width="120px" tooltip="none" :min="0" :max="this.maxBarSteps" :step="1" @dragging="handleScaleChange"/>            
                                </div>
                            </div> 
                            <div>
                                <Multiselect v-if="!onlyMyTasks"
                                    v-model="selectedMember" 
                                    :placeholder="this.$t('UI.select')"
                                    :multiple="true"
                                    :options="users" 
                                    :class="['cobalt-task__multiselect']"
                                    track-by="id"                              
                                    selectLabel=""
                                    label="first_name"
                                    selectedLabel=""
                                    deselectLabel="&#x2715;"
                                    open-direction="bottom"
                                    @remove="removePerformer"
                                >
                                    <template #selection="{ values, search, isOpen }">
                                        <div class="multiselect__single cobalt-profile-list__wrapper" v-show="!isOpen">
                                            <div class="cobalt-profile-list__item" v-for="item in values">
                                                <CobaltProfile 
                                                    :firstName="item.first_name" 
                                                    :lastName="item.last_name" 
                                                    :short="values.length > 1 ? true : false"
                                                    size="np-avatar-small"
                                                />
                                            </div>
                                        </div>
                                    </template>                            
                                    <template #option="props">
                                        <span class="np-font-s">
                                            {{ props.option.first_name + ' ' + props.option.last_name }}
                                        </span>
                                    </template>
                                </Multiselect> 
                            </div>          
                        </div>
                        <div class="np-panel-body h-100" style="position: relative; overflow: hidden;">
                                <Bar
                                    ref="barChartComponent"
                                    :key="this.barChartKey"
                                    :data="this.statusesChartData" 
                                    :options="chartOption"
                                />
                        </div>
                    </div>

                    <div class="row pt-4">
                        <div class="col">
                            <div class="np-header2 vertical-align-center np-pb-3">
                                <svg class="np-mr-2"><use href="@/assets/images/icons/team.svg#icon"></use></svg> {{ $t("UI.dashboard.projectSummary") }}
                            </div> 
                            <div class="grid-container">                            
                                <div class="np-widget">
                                    <div class="np-widget-top">
                                        <p class="np-font-l">{{ users.length }}</p>
                                        <svg><use href="@/assets/images/icons/team2.svg#icon"></use></svg>
                                    </div>
                                    <div class="np-widget-bottom">
                                        <span>{{ $t("UI.dashboard.projectMembers") }}</span>
                                    </div>
                                </div>
                        
                                <div class="np-widget">
                                    <div class="np-widget-top">
                                        <p class="np-font-l">{{ Math.round(this.taskList.length / users.length) }}</p>
                                        <svg><use href="@/assets/images/icons/team2-absent.svg#icon"></use></svg>
                                    </div>                        
                                    <div class="np-widget-bottom">
                                        <span>{{ $t("UI.dashboard.averageTaskCount") }}</span>
                                    </div>
                                </div>
                                
                                <div class="np-widget">
                                    <div class="np-widget-top">
                                        <p class="np-font-l">{{ this.tasksObject.completedInTime }}</p>
                                    </div>
                                    <div class="np-widget-bottom">
                                        <span>{{ $t("UI.dashboard.completeInTime") }}</span>
                                    </div>
                                </div>      

                                <div class="np-widget">
                                    <div class="np-widget-top">
                                        <p class="np-font-l">
                                            <template v-if="this.tasksObject.averageCompleteTime.days > 0">
                                                {{ this.tasksObject.averageCompleteTime.days }}{{ $t("UI.daysTo") }}&nbsp;  
                                            </template>
                                            <template v-else-if="this.tasksObject.averageCompleteTime.hours > 0">
                                                {{ this.tasksObject.averageCompleteTime.hours }}{{ $t("UI.hoursTo") }}&nbsp;
                                            </template>
                                            <template v-else>
                                                {{ this.tasksObject.averageCompleteTime.minutes }}{{ $t("UI.minutesTo") }}
                                            </template>                                            
                                        </p>
                                        <svg class="np-color-danger"><use href="@/assets/images/icons/time-hourglass.svg#icon"></use></svg>
                                    </div>                        
                                    <div class="np-widget-bottom">
                                        <span>{{ $t("UI.dashboard.averageTaskTime") }}</span>
                                    </div>
                                </div>

                                <div class="np-widget">
                                    <div class="np-widget-top">
                                        <p class="np-font-l">{{ this.tasksObject.occupied.count }}</p>
                                        <svg class="np-color-amber"><use href="@/assets/images/icons/time-progress.svg#icon"></use></svg>
                                    </div>                        
                                    <div class="np-widget-bottom">
                                        <span>{{ $t("UI.dashboard.filesInProject") }}</span>
                                    </div>
                                </div>   

                                <div class="np-widget">
                                    <div class="np-widget-top">
                                        <p class="np-font-l np-color-danger">{{ this.tasksObject.occupied.progress }}</p>
                                    </div>
                                    <div class="np-widget-bottom">
                                        <span>{{ $t("UI.dashboard.occupiedSpace") }}</span>
                                    </div>
                                </div>                                                        
                            </div>                            
                        </div>                        
                    </div>                   

            </div>

            <div class="col-lg-3 h-100 d-flex flex-column">
                <Tabs modelValue="comments">
                    <Tab id="comments" :title="$t('UI.task.comments')" :icon="require('@/assets/images/icons/comment.svg') + '#icon'">                   
                        <div class="np-panel flex-grow-1">
                            <div class="np-group np-group-column np-comments">
                                <SimpleBar data-simplebar-auto-hide="true" style="max-height: 100%;">
                                    <ul class="np-list">
                                        <li class="np-comment" v-for="item in this.projectComments.items" :key="item.id" role="button" @dblclick="showModTask(item.id, 0, 'comments')">
                                            <!--
                                            <div class="np-group justify-content-between np-mb-3 w-100">
                                                <div class="np-badge-s np-inactive np-font-xs np-mr-2">{{ item.task }}</div>
                                                <button class="np-btn np-btn-link"  @click="showModTask(item.taskId, item.statusId, 'comments')"><svg><use href="@/assets/images/icons/eye.svg#icon"></use></svg></button>
                                            </div>
                                            <div class="w-100" role="button" @dblclick="showModTask(item.taskId, item.statusId, 'comments')">
                                                <div class="np-group align-items-center">
                                                    <CobaltProfile 
                                                        :firstName="item.comment.user.name" 
                                                        lastName="" 
                                                        size="np-avatar-medium me-1"
                                                        :short="true"
                                                        :avatar="item.comment.avatar"
                                                    />
                                                    <div class="np-group np-group-column">
                                                        <span class="np-font-m np-f-medium">{{ item.comment.user.name }}</span>
                                                        <span class="np-font-xs np-color-light">{{ localeDateTime(item.comment.date_create) }}</span>
                                                    </div>
                                                </div>
                                                <div class="np-comment-text">
                                                    {{ item.comment.body }}
                                                </div>
                                            </div>
                                            <div class="np-group justify-content-between">
                                                <div class="np-group align-items-center" v-if="item.comment.reply">
                                                    <svg class="np-mr-1"><use href="@/assets/images/icons/comment.svg#icon"></use></svg>
                                                </div>                                            
                                            </div>
                                            -->
                                            <div class="np-font-m np-f-medium pb-2 w-100">
                                                {{ item.title }}
                                            </div>
                                            <div class="np-group align-items-center justify-content-between mb-1 w-100">
                                                <div class="np-group align-items-center">
                                                    <div class="unread me-2">
                                                        <svg><use href="@/assets/images/icons/comment.svg#icon"></use></svg>
                                                    </div>
                                                    <div class="np-font-m np-f-medium me-2">
                                                        {{ item.unread_count }}
                                                    </div>
                                                    <svg class="icon"><use href="@/assets/images/icons/eye.svg#icon"></use></svg>
                                                </div>
                                                <div class="np-font-s">
                                                    {{ localeDateTime(item.last_comment_date) }}
                                                </div>
                                            </div>                            
                                        </li>
                                    </ul>  
                                </SimpleBar>                              
                            </div>
                        </div>                    
                    </Tab>
                    <Tab id="notifications" :title="$t('UI.dashboard.notifications')" :icon="require('@/assets/images/icons/notification.svg') + '#icon'">
                        <div class="np-panel flex-grow-1">
                            <div class="np-group np-group-column np-comments">
                                <SimpleBar data-simplebar-auto-hide="true" style="max-height: 100%;">
                                    <ul class="np-list">
                                        <li class="np-comment" v-for="item in notificationStore.notifications" :key="item.id">
                                            <div class="np-group align-items-center">
                                                <div class="np-group">
                                                    <span class="np-font-xs me-2 np-color-light">{{ localeDateTime(item.created_at) }}</span>
                                                    <span class="np-font-xs">{{ item.model_name }}</span>
                                                </div>
                                            </div>
                                            <div class="np-font-s">
                                                <span :class="item.is_read ? 'np-color-light' : 'np-f-bold'">{{ item.message }}</span>
                                            </div>
                                        </li>
                                    </ul>  
                                </SimpleBar>                              
                            </div>
                        </div> 
                    </Tab>
                </Tabs>
                <!--
                <div class="dashboard-widget" style="height: 100vh;">
                    <div class="dashboard-widget__title">
                        <div class="w-100 mb-2">
                            <ul class="nav nav-tabs cobalt-task__nav-tabs" id="myTab" role="tablist">
                                <li class="nav-item" role="presentation">
                                    <button class="nav-link" :class="{ active: wActiveTab == 'wComments' }" @click="setWidgetActiveTab('wComments')" id="files-tab" data-bs-toggle="tab" data-bs-target="#wComments" type="button" role="tab" aria-controls="home" aria-selected="true">
                                        {{ $t("UI.dashboard.messages") }}
                                    </button>
                                </li>

                                <li class="nav-item" role="presentation">
                                    <button class="nav-link" :class="{ active: wActiveTab == 'wActivities' }" @click="setWidgetActiveTab('wActivities')" id="files-tab" data-bs-toggle="tab" data-bs-target="#wActivities" type="button" role="tab" aria-controls="home" aria-selected="true">
                                        Activities
                                    </button>
                                </li>   
                            </ul>
                        </div>                             
                    </div>
                    <div class="dashboard-widget__body tab-content" id="wTabContent">
                        <div class="tab-pane fade show h-100" :class="{ active: wActiveTab === 'wComments' }" id="wComments" role="tabpanel" aria-labelledby="wComments-tab">
                            <div class="cobalt-comments__list">
                                <div class="comment-wrapper" v-for="item in this.messages" :key="item.comment.id">
                                    <div class="comment flex-column align-content-center">
                                        <span class="text-end text-link" @click="showModTask(item.taskId, item.statusId, 'comments')">
                                            {{ item.task }} {{ localeDateTime(item.comment.date_create) }}
                                        </span>
                                        <div class="comment-content flex-grow-1 p-2">
                                            <div class="replyToComment" v-if="item.comment.reply">
                                                <div class="replyToComment__content">
                                                    <span class="author">{{ item.comment.reply.user.name }}</span> 
                                                    {{ item.comment.reply.body }}
                                                </div>                                                    
                                            </div>
                                            <p class="author">{{ item.comment.user.name }}</p> 
                                            <p class="small mb-0">
                                                {{ item.comment.body }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="tab-pane fade show h-100" :class="{ active: wActiveTab === 'wActivities' }" id="wActivities" role="tabpanel" aria-labelledby="wActivities-tab">
                            <div class="cobalt-activities__list">
                                <div class="project-activity">
                                    <div class="project-activity__name">
                                        <h3>Ellie joined team developers</h3>
                                        <span>04 April, 2021 | 04:00 PM</span>
                                    </div>                                           
                                </div>

                                <div class="project-activity">
                                    <div class="project-activity__name">
                                        <h3>Jenny joined team HR</h3>
                                        <span>04 April, 2021 | 04:00 PM</span>
                                    </div>                                           
                                </div>
                                
                                <div class="project-activity">
                                    <div class="project-activity__name">
                                        <h3>Adam got employee of the month</h3>
                                        <span>04 April, 2021 | 04:00 PM</span>
                                    </div>                                           
                                </div>
                                
                                <div class="project-activity">
                                    <div class="project-activity__name">
                                        <h3>Robert joined team design</h3>
                                        <span>04 April, 2021 | 04:00 PM</span>
                                    </div>                                           
                                </div> 
                            </div>
                        </div>
                    </div> 
                </div>                       
                -->
            </div>           
        </div>     
    </div>
</template>

<script>

import { useUserStore } from '@/stores/userStore'
import { useNotificationStore } from "@/stores/notificationStore"
import { ref } from 'vue';
import { Bar, Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { localeDate, localeDateTime, formatBytes, convertSeconds } from '@/utils/format'
import TaskCardSimple  from "@/components/TaskCardSimple.vue"
import TaskCardAccordion  from "@/components/TaskCardAccordion.vue" 
import CobaltProfile   from "@/components/dashboard/parts/CobaltProfile.vue"
import Tabs from "@/components/elements/tabs/Tabs.vue"
import Tab from "@/components/elements/tabs/Tab.vue"
import IndicatorDaysTo  from "@/components/elements/IndicatorDaysTo.vue"
import Accordion from '@/components/elements/Accordion.vue'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/antd.css'
import Multiselect from 'vue-multiselect'
import { mapGetters, mapActions } from 'vuex';
import Config from '../../../config.js'
import SimpleBar from 'simplebar-vue';
import 'simplebar-vue/dist/simplebar.min.css';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, ChartDataLabels)

export default {
    components: {
        TaskCardSimple,
        TaskCardAccordion,
        CobaltProfile,
        Bar,
        Doughnut,
        VueSlider,
        IndicatorDaysTo,
        Accordion,
        SimpleBar,
        Multiselect,
        Tabs, Tab
    },
    props: {
        project: {
            type: Object,
            default: {
                id: 0,
                name: '',
                description: '',
                date_create: null,
                date_update: null,
                date_planned_end: null,
                company_id: null,
                budget: null
            }
        },      
        statuses: {
            type: Array,
            default: []
        },  
        projectComments: {
            type: Array,
            default: []
        },                  
        tasks: {
            type: Object,
            default: {}
        },  
        users: {
            type: Array,
            default: []
        },            
        priorityValues: {
            type: Array,
            default: []
        }
    },
    data() {
        return {
            Config,
            compInfo: null,
            members: [],
            selectedMember: null,
            taskFilter: '',
            filterVisible: false,
            barChartScale: 0,
            barChartKey: 0,
            wActiveTab: 'wComments',
            accordionId: 1,
            onlyMyTasks: false
        }
    },
    async beforeMount() {
        await this.notificationStore?.init()   
    },    
    computed: {
        userStore() {
            return useUserStore()
        },
        notificationStore() {
            return useNotificationStore()
        },         
        maxBarSteps: {
            get() {   
                return Math.max(0, this.statuses.length - 7)
            }
        },
        chartOption: {
            get() {
                return {
                    plugins: {
                        datalabels: {
                            anchor: (context) => {
                                const yAxis = context.chart.scales.y;
                                const basePixel = yAxis.getPixelForValue(0);
                                const valuePixel = yAxis.getPixelForValue(context.dataset.data[context.dataIndex]);
                                const barHeight = basePixel - valuePixel;
                                return barHeight < 25 ? 'start' : 'end';
                            },
                            align: (context) => {
                                const yAxis = context.chart.scales.y;
                                const basePixel = yAxis.getPixelForValue(0);
                                const valuePixel = yAxis.getPixelForValue(context.dataset.data[context.dataIndex]);
                                const barHeight = basePixel - valuePixel;
                                return barHeight < 25 ? 'top' : 'end';
                            },
                            offset: (context) => {
                                const yAxis = context.chart.scales.y;
                                const basePixel = yAxis.getPixelForValue(0);
                                const valuePixel = yAxis.getPixelForValue(context.dataset.data[context.dataIndex]);
                                const barHeight = basePixel - valuePixel;
                                return barHeight < 25 ? barHeight : -25;
                            },
                            color: (context) => {
                                const yAxis = context.chart.scales.y;
                                const basePixel = yAxis.getPixelForValue(0);
                                const valuePixel = yAxis.getPixelForValue(context.dataset.data[context.dataIndex]);
                                const barHeight = basePixel - valuePixel;
                                return barHeight < 25 ? '#000' : '#fff';
                            },
                            font: {
                                size: 14,
                                weight: '500',
                            },
                            formatter: (value) => value + "",
                        },                    
                        legend: {
                            display: false
                        }                  
                    },
                    borderWidth: 0,
                    borderRadius: 5,
                    scales: {
                        y: {
                            display: false,
                            grid: {
                                display: false
                            }
                        },
                        x: {
                            display: true,
                            min: this.barChartScale,
                            max: 6 + this.barChartScale,  
                            grid: {
                                display: false
                            },
                            border: {
                                display: false,
                            },
                            ticks: {
                                autoSkip: false,   
                                callback: function (value, index) {
                                    const absoluteIndex = index + this.chart.options.scales.x.min
                                    const label = this.chart.data.labels[absoluteIndex]
                                    return label.length > 12 ? label.substring(0, 12) + "..." : label
                                },       
                                maxRotation: 0,
                                font: {
                                    size: 11,
                                }
                            }
                        }
                    },                
                    autoPadding: true,
                    responsive: true,
                    barPercentage: 0.65,
                    maintainAspectRatio: false,                
                }                
            }
        },  
        deadlineProject: {
            get() {
                let date = 0
                
                // Here add checking for project date_planned_date
                if (this.project && this.project.date_planned_end) {
                    return this.project.date_planned_end
                }

                this.statuses.forEach(element => {
                    element.tasks.forEach(taskItem => {
                        if (taskItem.date_planned_end && taskItem.date_planned_end > date) {
                            date = taskItem.date_planned_end
                        }
                    })
                })          
                return date        
            }
        },
        taskList: {
            get() {
                let list = []
                
                this.statuses.forEach(element => {
                    element.tasks.forEach(taskItem => {
                        if (taskItem.title.includes(this.taskFilter) || taskItem.description.includes(this.taskFilter)) {
                            let item = { "status": element, "task": taskItem }
                            if (this.onlyMyTasks) {
                                if (taskItem.performer.find(p => p.id === this.userStore.user.id)) {
                                    list.push(item)    
                                }
                            } else { 
                                list.push(item)
                            }
                        }
                    })
                })

                return list
            }
        },    
        statusesChartData: {
            get() {
                let chartData = { 
                    backgroundColor: [],
                    data: [],
                    borderSkipped: false,
                    borderRadius: 8
                }
                let labels = []

                this.statuses.forEach(element => {
                    labels.push(element.title + "")
                    chartData.backgroundColor.push(element.color)

                    if (this.selectedMember?.length > 0 || this.onlyMyTasks) {
                        let count = 0
                        element.tasks.forEach(task => {
                            let hasIntersection = false
                            if (this.onlyMyTasks) {
                                hasIntersection = task.performer.find(p => p.id === this.userStore.user.id)
                            } else {
                                hasIntersection = task.performer.some(p => this.selectedMember.find(m => m.id === p.id))
                            }
                            if (hasIntersection) count++
                        })
                        chartData.data.push(count)
                    } else {
                        chartData.data.push(this.tasks[element.id].length)
                    }
                })                

                if (this.statuses.length < 7) {
                    labels.push("")
                    chartData.data.push(null)
                }
                
                let res = {
                    labels: labels,
                    datasets: [
                        chartData
                    ]
                }

                return res
            }
        },
        tasksObject: {
            get() {
                let result = {
                    today: [],
                    expired: [],
                    failed: [],
                    completed: [],
                    completedInTime: 0,
                    averageCompleteTime: {},
                    controlled: [],
                    occupied: {
                        count: 0,
                        value: 0,
                        progress: "0%"
                    }
                }    
                let today = new Date().getTime() / 1000
                let space = 0
                let count = 0
                let averageCompleteTime = 0
                const userId = this.userStore.user?.id

                for (var key in this.tasks) {
                    this.tasks[key].forEach(task => {
                        if (task.controller.find(p => p.id === userId)) {
                            result.controlled.push(task)
                        }

                        if (this.onlyMyTasks) {
                            if (!task.performer.find(p => p.id === userId)) {
                                return   
                            }
                        }
                        // Calculate used disk space before check task complete, sum all files in project 
                        // If we need exclude completed task, move this block at the bottom
                        // !!! Occupied
                        for(var fKey in task.files) {
                            space += task.files[fKey].fileSize
                        }

                        count += task.files.length

                        task.subtask.forEach(subTask => {
                            if (subTask.files.length > 0) {
                                count += subTask.files.length
                                for(var fKey in subTask.files) {
                                    space += subTask.files[fKey].fileSize
                                }                                
                            }
                        })                         
                        // !!!

                        if (!!task.date_actual_end) {
                            result.completed.push(task)
                            if (task.date_actual_end < task.date_planned_end) {
                                result.completedInTime++
                            }
                            const start = task.date_planned_start ? task.date_planned_start : task.date_create
                            averageCompleteTime += task.date_actual_end - start
                        }
                        const start = task.date_planned_start
                        const end   = task.date_planned_end

                        // Ignore all completed tasks
                        if (!!task.date_actual_end) return

                        // today
                        if ( !start) {
                            if (end) {
                                if (today < end) {
                                    result.today.push(task)
                                }
                            } else {
                                result.today.push(task)
                            }
                        } else 
                            if (!end) {
                                if (start < today) {
                                    result.today.push(task)
                                }
                            } else {
                                if (start < today && end > today) {
                                    result.today.push(task)
                                }
                            }
                        // Expired
                        if (start > 0 && end > 0) {
                            const section = ( end - start ) / 3
                            const current =  new Date().getTime() / 1000
                            
                            if (end - section < current && current < end) {
                                result.expired.push(task)
                            }                                
                        }                        
                        // failed
                        if ( start > 0 && end > 0 && end < today) {
                            result.failed.push(task)
                        } 
                    })                       
                }
                
                result.occupied = {
                    count: count,
                    value: formatBytes(space),
                    progress:  Math.round(space * 100 / ( 1024 * 1024) / 200) + '%'
                }     

                if (result.completed.length > 0) {
                    result.averageCompleteTime = this.convertSeconds(Math.round(averageCompleteTime / result.completed.length), "object")
                }

                return result
            }
        },
        accordionSections: {
            get() {
                return [
                    {   
                        id: 1,
                        title: this.$t("UI.dashboard.toToday"),
                        tasks: this.tasksObject.today
                    }, 
                    {
                        id: 2,
                        title: this.$t("UI.dashboard.toExpired"),
                        tasks: this.tasksObject.expired
                    },
                    {
                        id: 3,
                        title: this.$t("UI.dashboard.toFailed"),
                        tasks: this.tasksObject.failed
                    },
                    {
                        id: 4,
                        title: this.$t("UI.dashboard.toControlled"),
                        tasks: this.tasksObject.controlled
                    }
                ]
            }
        },
        accordionOptions() {
            const result = {}
            this.accordionSections.forEach(section => {
                result[section.id] = {
                    state: this.accordionId === section.id ? 'expanded' : 'collapsed',
                    size: 'normal',
                    selected: false,
                    hideArrow: section.tasks.length == 0,
                    onlyArrow: false,
                    additionalClasses: 'bordered'
                }
            })
            return result
        },        
        messages: {
            get() {
                let result = []

                this.statuses.forEach(element => {
                    element.tasks.forEach(taskItem => {
                        for(var fKey in taskItem.comments) {
                            result.push({ "task": taskItem.title, "taskId": taskItem.id, "statusId": element.id, "comment": taskItem.comments[fKey] })
                        } 
                    })
                })  
                 
                result.sort((a,b) => a.comment.date_create - b.comment.date_create)

                return result
            }
        },
        projectProgress: {
            get() {
                let count = 0
                let completed = 0

                this.statuses.forEach(element => {
                    element.tasks.forEach(taskItem => {
                        count++
                        if (taskItem.date_actual_end) {
                            completed++
                        }
                    })
                })

                return count > 0 ? Math.round(completed * 100 / count) : 0
            }
        }                 
    },
    mounted() {
        const debouncedHandleResize = this.debounce(this.handleResize, 200);
        window.addEventListener('resize', debouncedHandleResize);
        this.debouncedHandleResize = debouncedHandleResize;
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },    
    methods: {
        ...mapActions('company', ['list', 'addCompany', 'delete']),
        ...mapGetters('company', ['getList']),

        localeDate,
        localeDateTime,  
        convertSeconds,
         
        toggleAccordion(id) {
            this.accordionId = id
        },
        doOnlyMyTasks(flag) {
            this.onlyMyTasks = flag
        },
        userChartData(id) {
            let data = {
                datasets: [
                    {
                        backgroundColor: ['#CCC', '#41B883', '#DD1B16'],
                        data: []
                    }
                ]
            }

            let assignedTasks = 0, completedTasks = 0, expiredTasks = 0
            let today = new Date().getTime() / 1000

            for (var key in this.tasks) {
                this.tasks[key].forEach(task => {

                    if (task.performer.find(u => u.id === id)) {
                        return
                    }

                    assignedTasks++

                    if (!!task.date_actual_end) {
                        completedTasks++
                    }
                    const start = task.date_planned_start
                    const end   = task.date_planned_end

                    // Expired
                    if (start > 0 && end > 0) {
                        const section = ( end - start ) / 3
                        const current =  new Date().getTime() / 1000
                        
                        if ( start > 0 && end > 0 && end < today) {
                            expiredTasks++
                        }                                
                    }                        
                })                       
            }

            data.datasets[0].data = [assignedTasks, completedTasks, expiredTasks]

            return data
        },                   
        getPreparedTask(task) {
            const priorityObj = this.priorityValues.find(option => option.value === task.priority)
            return {
                ...task,
                priorityObj
            }
        }, 
        getTaskStatus(task) {
            let status = {}

            this.statuses.forEach(element => {
                element.tasks.forEach(taskItem => {
                    if (taskItem.id == task.id) {
                        status = { ... element }
                    }
                })
            })

            return status
        }, 
        handleScaleChange(value, index) {
            this.barChartScale = value
            this.barChartKey++
        },
        showFilters() {
            this.filterVisible = !this.filterVisible
        }, 
        showModTask(taskId, statusId, taskActiveTab="params") {
            this.$emit('showModTask', taskId, statusId, taskActiveTab)
        },
        showModProject() {
            this.$emit('showModProject')
        },
        setWidgetActiveTab(tab) {
          this.wActiveTab = tab
        },     
        refresh() {
            this.$emit('refresh')
        },
        debounce(func, delay) {
            let timeoutId;
            return function (...args) {
                clearTimeout(timeoutId)
                timeoutId = setTimeout(() => func.apply(this, args), delay)
            }
        },
        handleResize() {
            this.barChartKey++
        }
    }

}
</script>

<style>
  .grid-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr); /* 3 равные колонки */
    gap: 1rem; /* Отступы между ячейками */
  }
  .grid-container .np-widget {
    max-width: 100%;
  }
</style>